import React from 'react';
import { Alert, Badge, Card, Table } from 'react-bootstrap';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import superagent from 'superagent';

/**
 * Simulations tab (superKudu only): the outcome of the model's deployment-impact
 * runs — the latest one rendered like the report email (before/after transition
 * matrix, per-segment totals and % change), plus the last 10 as history.
 * Data is reported back by the deployment_impact_analysis DAG.
 */

type MatrixRow = [string, string, number];

type ImpactResult = {
  result_ref: string;
  status: 'success' | 'error';
  model_id: number | null;
  reference_model_id: number | null;
  model_type: string | null;
  matrix: MatrixRow[] | null;
  error: string | null;
  created_at: string;
};

type MatchParams = { tenant: string; modelId: string };

type SimulationsPageState = {
  results: ImpactResult[] | null;
  error: string | null;
  selectedRef: string | null;
};

/** Distinct segment labels of a matrix, in their emailed order ("1 - very good" sorts first). */
function segmentsOf(matrix: MatrixRow[]): string[] {
  const labels = new Set<string>();
  matrix.forEach(([before, after]) => {
    if (before) labels.add(before);
    if (after) labels.add(after);
  });
  return Array.from(labels).sort();
}

/** Strip the sort prefix for display: "1 - very good" -> "very good". */
function segmentName(label: string): string {
  return label.replace(/^\d+ - /, '');
}

function MovementMatrix({ matrix }: { matrix: MatrixRow[] }) {
  const segments = segmentsOf(matrix);
  const counts = new Map<string, number>();
  matrix.forEach(([before, after, count]) => {
    counts.set(`${before}|${after}`, count);
  });
  // Same numbers the email shows: totals per segment before/after and % change.
  const beforeTotals = new Map<string, number>();
  const afterTotals = new Map<string, number>();
  matrix.forEach(([before, after, count]) => {
    beforeTotals.set(before, (beforeTotals.get(before) ?? 0) + count);
    afterTotals.set(after, (afterTotals.get(after) ?? 0) + count);
  });

  return (
    <>
      <Table bordered size="sm" className="w-auto">
        <thead>
          <tr>
            <th>before \ after</th>
            {segments.map((s) => (
              <th key={s}>{segmentName(s)}</th>
            ))}
            <th className="table-secondary">total before</th>
          </tr>
        </thead>
        <tbody>
          {segments.map((before) => (
            <tr key={before}>
              <th>{segmentName(before)}</th>
              {segments.map((after) => {
                const count = counts.get(`${before}|${after}`) ?? 0;
                const moved = before !== after && count > 0;
                return (
                  <td key={after} className={moved ? 'table-warning' : ''}>
                    {count}
                  </td>
                );
              })}
              <td className="table-secondary">
                {beforeTotals.get(before) ?? 0}
              </td>
            </tr>
          ))}
          <tr>
            <th className="table-secondary">total after</th>
            {segments.map((s) => {
              const before = beforeTotals.get(s) ?? 0;
              const after = afterTotals.get(s) ?? 0;
              const pct =
                Math.round((after / (before || 1)) * 10000) / 100 - 100;
              return (
                <td key={s} className="table-secondary">
                  {after}{' '}
                  <span
                    className={
                      pct > 0
                        ? 'text-success'
                        : pct < 0
                        ? 'text-danger'
                        : 'text-muted'
                    }
                  >
                    ({pct > 0 ? '+' : ''}
                    {pct}%)
                  </span>
                </td>
              );
            })}
            <td className="table-secondary" />
          </tr>
        </tbody>
      </Table>
      <p className="text-muted small">
        Rows: segment with the current model. Columns: segment with the
        simulated model. Highlighted cells are records that would change
        segment; (%) is the segment&apos;s size change.
      </p>
    </>
  );
}

function ResultCard({ result }: { result: ImpactResult }) {
  return (
    <Card className="mb-3">
      <Card.Body>
        <Card.Title className="d-flex align-items-center gap-2">
          Simulation of {new Date(result.created_at).toLocaleString()}
          <Badge bg={result.status === 'success' ? 'success' : 'danger'}>
            {result.status}
          </Badge>
        </Card.Title>
        <div className="text-muted small mb-2">
          model {result.model_id ?? '?'} vs{' '}
          {result.reference_model_id
            ? `model ${result.reference_model_id}`
            : 'live primary model'}
          {result.model_type ? ` · ${result.model_type}` : ''}
        </div>
        {result.status === 'error' ? (
          <Alert variant="danger" className="mb-0">
            {result.error || 'The simulation failed.'}
          </Alert>
        ) : result.matrix?.length ? (
          <MovementMatrix matrix={result.matrix} />
        ) : (
          <Alert variant="secondary" className="mb-0">
            The simulation finished but produced no movement rows (no scored
            records matched the comparison scope).
          </Alert>
        )}
      </Card.Body>
    </Card>
  );
}

const SimulationsPage = withRouter(
  class SimulationsPage extends React.Component<
    RouteComponentProps<MatchParams>,
    SimulationsPageState
  > {
    constructor(props: RouteComponentProps<MatchParams>) {
      super(props);
      this.state = { results: null, error: null, selectedRef: null };
    }

    async componentDidMount() {
      const { tenant, modelId } = this.props.match.params;
      try {
        const response = await superagent.get(
          `/api/tenant/${tenant}/model/${modelId}/impact_results`
        );
        this.setState({ results: response.body ?? [] });
      } catch (e: any) {
        this.setState({
          error: e?.message || 'Could not load the simulation history.',
        });
      }
    }

    render() {
      const { results, error, selectedRef } = this.state;
      if (error) return <Alert variant="danger">{error}</Alert>;
      if (results === null) return <p className="text-muted p-3">Loading…</p>;
      if (!results.length) {
        return (
          <Alert variant="secondary" className="m-3">
            No simulations yet for this model. Launch a deployment impact
            analysis to see its result here (it is also emailed).
          </Alert>
        );
      }
      const selected =
        results.find((r) => r.result_ref === selectedRef) ?? results[0];
      const history = results;
      return (
        <div className="p-3">
          <ResultCard result={selected} />
          <h5>History (last {history.length})</h5>
          <Table hover size="sm" className="w-auto">
            <thead>
              <tr>
                <th>when</th>
                <th>status</th>
                <th>compared against</th>
              </tr>
            </thead>
            <tbody>
              {history.map((r) => (
                <tr
                  key={r.result_ref}
                  role="button"
                  className={
                    r.result_ref === selected.result_ref ? 'table-active' : ''
                  }
                  onClick={() => this.setState({ selectedRef: r.result_ref })}
                >
                  <td>{new Date(r.created_at).toLocaleString()}</td>
                  <td>
                    <Badge bg={r.status === 'success' ? 'success' : 'danger'}>
                      {r.status}
                    </Badge>
                  </td>
                  <td>
                    {r.reference_model_id
                      ? `model ${r.reference_model_id}`
                      : 'live primary model'}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      );
    }
  }
);

export default SimulationsPage;
